import React, { useState, useEffect } from 'react';
import './BidEditModal.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.alooma.mg';

// Digits only -> comma-grouped string for money inputs
const formatMoney = (val) => {
  const digits = String(val ?? '').replace(/[^\d]/g, '');
  if (!digits) return '';
  return parseInt(digits, 10).toLocaleString('en-US');
};

function BidEditModal({ bidId, onClose, onUpdate }) {
  const [bid, setBid] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    amount: '',
    message: '',
    deliveryDays: '',
  });
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchBid();
  }, [bidId]);

  const fetchBid = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/bids/${bidId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setBid(data.bid);
        setFormData({
          amount: data.bid.amount || '',
          message: data.bid.message || '',
          deliveryDays: data.bid.deliveryDays || '',
        });
      } else {
        setError(data.message || 'Failed to load bid');
      }
    } catch (err) {
      setError('Failed to load bid');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    const token = localStorage.getItem('token');
    setIsSaving(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/bids/${bidId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setBid({ ...bid, ...formData });
        setIsEditing(false);
        if (onUpdate) onUpdate();
      } else {
        setError(data.message || 'Failed to update bid');
      }
    } catch (err) {
      setError('Failed to update bid');
    } finally {
      setIsSaving(false);
    }
  };

  const handleWithdraw = async () => {
    if (!window.confirm('Are you sure you want to withdraw this bid? This action cannot be undone.')) {
      return;
    }

    const token = localStorage.getItem('token');
    setIsSaving(true);

    try {
      const response = await fetch(`${API_URL}/api/bids/${bidId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        if (onUpdate) onUpdate();
        onClose();
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to withdraw bid');
      }
    } catch (err) {
      setError('Failed to withdraw bid');
    } finally {
      setIsSaving(false);
    }
  };

  const getContentTypeLabel = (type) => {
    const types = {
      feed: 'Feed Post',
      story: 'Story',
      reel: 'Reel',
      carousel: 'Carousel',
      mixed: 'Mixed Content',
    };
    return types[type] || type;
  };

  const getStatusClass = (status) => {
    return `status-badge status-${status}`;
  };

  if (isLoading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="bid-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-loading">
            <div className="loader"></div>
            <p>Loading bid details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!bid) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="bid-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-error">
            <p>{error || 'Bid not found'}</p>
            <button onClick={onClose} className="modal-close-btn">Close</button>
          </div>
        </div>
      </div>
    );
  }

  const isPending = bid.status === 'pending';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="bid-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Header */}
        <div className="bid-modal-header">
          <div className="header-top">
            <span className={getStatusClass(bid.status)}>{bid.status}</span>
            <span className="bid-date">
              Submitted {new Date(bid.createdAt).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </span>
          </div>
          <h2>{bid.jobTitle}</h2>
          <p className="brand-name">by {bid.brandName}</p>
        </div>

        {/* Job Summary */}
        <div className="job-summary">
          <div className="summary-item">
            <span className="summary-label">Budget</span>
            <span className="summary-value">${bid.jobBudget?.toLocaleString()}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Content</span>
            <span className="summary-value">{getContentTypeLabel(bid.contentType)}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Posts</span>
            <span className="summary-value">{bid.postCount}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Deadline</span>
            <span className="summary-value">
              {bid.jobDeadline
                ? new Date(bid.jobDeadline).toLocaleDateString()
                : 'Flexible'}
            </span>
          </div>
        </div>

        {/* Bid Details */}
        <div className="bid-details-section">
          <h3>Your Bid</h3>

          {error && <div className="error-message">{error}</div>}

          {isEditing ? (
            <div className="bid-form">
              <div className="form-group">
                <label>Bid Amount ($)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={formatMoney(formData.amount)}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value.replace(/[^\d]/g, '') })}
                  placeholder="Enter your bid amount"
                />
              </div>
              <div className="form-group">
                <label>Delivery Time (days)</label>
                <input
                  type="number"
                  value={formData.deliveryDays}
                  onChange={(e) => setFormData({ ...formData, deliveryDays: e.target.value })}
                  placeholder="Number of days"
                />
              </div>
              <div className="form-group">
                <label>Your Proposal</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Describe how you'll approach this campaign..."
                  rows={5}
                />
              </div>
            </div>
          ) : (
            <div className="bid-display">
              <div className="bid-stats">
                <div className="bid-stat">
                  <span className="stat-value amount">${bid.amount?.toLocaleString()}</span>
                  <span className="stat-label">Your Bid</span>
                </div>
                <div className="bid-stat">
                  <span className="stat-value">{bid.deliveryDays || '—'}</span>
                  <span className="stat-label">Days to Deliver</span>
                </div>
              </div>
              <div className="bid-proposal">
                <h4>Your Proposal</h4>
                <p>{bid.message || 'No proposal message provided.'}</p>
              </div>
            </div>
          )}
        </div>

        {/* Status Messages */}
        {bid.status === 'accepted' && (
          <div className="status-message success">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            <div>
              <strong>Congratulations!</strong>
              <p>Your bid has been accepted. The brand will be in touch soon.</p>
            </div>
          </div>
        )}

        {bid.status === 'rejected' && (
          <div className="status-message rejected">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
            <div>
              <strong>Bid Not Selected</strong>
              <p>Unfortunately, the brand went with another creator for this campaign.</p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="bid-modal-actions">
          {isPending && (
            <>
              {isEditing ? (
                <>
                  <button
                    className="action-btn secondary"
                    onClick={() => {
                      setIsEditing(false);
                      setFormData({
                        amount: bid.amount || '',
                        message: bid.message || '',
                        deliveryDays: bid.deliveryDays || '',
                      });
                    }}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                  <button
                    className="action-btn primary"
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="action-btn danger"
                    onClick={handleWithdraw}
                    disabled={isSaving}
                  >
                    Withdraw Bid
                  </button>
                  <button
                    className="action-btn primary"
                    onClick={() => setIsEditing(true)}
                  >
                    Edit Bid
                  </button>
                </>
              )}
            </>
          )}
          {!isPending && (
            <button className="action-btn secondary" onClick={onClose}>
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default BidEditModal;


